/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/alt-text */
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { tss } from 'tss-react/mui';
import { i18n } from 'src/common/i18n/i18n';
import { Box, Divider, Typography } from '@mui/material';
import { numberPrettier } from 'src/common/utils/formatters';
import { getSortedReactions, getSourceFromReaction } from 'src/common/utils/utils';
import { CommentsIcon, SessionBeginIcon, ShareIcon } from '@cellebrite/saas-ui-icons';
import _, { isArray } from 'lodash';
import { Tooltip, ElementsDivider, COLORS } from '@cellebrite/saas-ui-common';
import Topic from './components/Topic/Topic';
import { GlobalContext } from 'src/common/contexts/globalContext';
import { SECTION } from 'src/common/constants/constants';

interface PostInteractionsProps {
    post: any;
    onReactionsClick?: any;
    onCommentsClick?: any;
}

const PostInteractions: React.FC<PostInteractionsProps> = ({ post, onReactionsClick, onCommentsClick }) => {
    const { reportData } = useContext(GlobalContext);
    const containerRef = useRef(null);
    const tooltipWidthRef = useRef(null);
    const [tooltipTopicsCount, setTooltipTopicsCount] = React.useState(0);
    const isReactionsExists =
        isArray(post?.reactionsCountData) &&
        post?.reactionsCountData.filter((reactionsCount) => reactionsCount.collected).length;
    const { classes: c } = useStyles({
        commentsExists: Boolean(post.commentsCountData?.collected),
        reactionsExists: isReactionsExists,
    });
    const commentsContainer = useMemo(() => {
        return {
            backgroundColor:
                post.watchlistComments?.filter(
                    (watchList) =>
                        watchList.hasWatchlistInComments &&
                        (!reportData.currentHighlightsWatchlist?.id ||
                            watchList.watchlistId === reportData.currentHighlightsWatchlist.id),
                ).length && reportData.displayHighlights
                    ? post.contentId === reportData.currentHighlightsLink?.id &&
                      'comments' === reportData.currentHighlightsLink?.type &&
                      reportData.currentHighlightsLink?.section === SECTION.POSTS
                        ? COLORS.NEGATIVE
                        : COLORS.NEGATIVE_SOFT
                    : null,
        };
    }, [
        post.contentId,
        post.watchlistComments,
        reportData.currentHighlightsLink,
        reportData.displayHighlights,
        reportData.currentHighlightsWatchlist,
    ]);
    const reactionsContainer = useMemo(() => {
        return {
            visibility: post.counts.reactions ? null : 'hidden',
            backgroundColor:
                post.watchlistReactions?.filter(
                    (watchList) =>
                        watchList.hasWatchlistInReactions &&
                        (!reportData.currentHighlightsWatchlist?.id ||
                            watchList.watchlistId === reportData.currentHighlightsWatchlist.id),
                ).length && reportData.displayHighlights
                    ? post.contentId === reportData.currentHighlightsLink?.id &&
                      'reactions' === reportData.currentHighlightsLink?.type &&
                      reportData.currentHighlightsLink?.section === SECTION.POSTS
                        ? COLORS.NEGATIVE
                        : COLORS.NEGATIVE_SOFT
                    : null,
        };
    }, [
        post.contentId,
        post.watchlistComments,
        reportData.currentHighlightsLink,
        reportData.displayHighlights,
        reportData.currentHighlightsWatchlist,
    ]);

    const getInteractions = () => {
        const result = [];
        if (post.counts.comments) {
            result.push(
                <Tooltip
                    content={
                        <Typography>{`List may not include all comments - ${post.commentsCountData.collected} out of ${post.commentsCountData.total}`}</Typography>
                    }
                >
                    <Box
                        sx={commentsContainer}
                        className={c.commentsContainer}
                        onClick={(event) => {
                            if (post.commentsCountData?.collected) {
                                onCommentsClick?.(event);
                            }
                        }}
                    >
                        <CommentsIcon />
                        <Typography sx={{ ml: 0.5 }} variant="s14w500">
                            {numberPrettier(post.counts.comments)}
                        </Typography>
                    </Box>
                </Tooltip>,
            );
        }
        if (post.counts.shares) {
            result.push(
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ShareIcon className={c.shareIcon} />
                    <Typography sx={{ ml: 0.5 }} variant="s14w500" className="color-md">
                        {numberPrettier(post.counts.shares)}
                    </Typography>
                </Box>,
            );
        }
        return result;
    };
    const allReactions = _.find(post.reactionsCountData, { reactionType: 'reactions' });
    const sortedReactions = useMemo(() => {
        return getSortedReactions(post);
    }, [post.reactionsCountData, post.reactionsCounts]);

    useEffect(() => {
        if (containerRef.current?.offsetHeight > 32) {
            setTooltipTopicsCount(tooltipTopicsCount + 1);
        } else if (tooltipTopicsCount > post.labels?.length) {
            setTooltipTopicsCount(0);
        }
    }, [post, tooltipTopicsCount]);

    return (
        <Box sx={{ height: 77 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, justifyContent: 'space-between' }}>
                <Tooltip
                    styles={{
                        maxWidth: tooltipWidthRef?.current
                            ? tooltipWidthRef.current.offsetWidth + 32
                            : window.innerWidth,
                    }}
                    content={
                        allReactions ? (
                            <Box sx={{ mt: 1 }}>
                                <Box
                                    ref={tooltipWidthRef}
                                    sx={{
                                        display: 'flex',
                                        columnGap: 2,
                                        justifyContent: 'center',
                                        position: 'relative',
                                    }}
                                >
                                    {sortedReactions.map((reaction, index) => {
                                        const src = getSourceFromReaction(post.platform, reaction.type);
                                        return src ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                                key={index}
                                            >
                                                <img src={src} title={reaction.type} />
                                                <Typography sx={{ ml: 1 }} variant="s14w500" className="color-bd">
                                                    {`${reaction.collected}/`}
                                                </Typography>
                                                <Typography variant="s14w700" className="color-bd">
                                                    {reaction.total}
                                                </Typography>
                                            </Box>
                                        ) : null;
                                    })}
                                </Box>
                                <Divider sx={{ mt: 1, mb: 1 }} className="border-color-ms" />
                                <Typography variant="s12w400" className="color-ms">
                                    {i18n.translate('REACTIONS_LABEL', {
                                        collected: allReactions?.collected,
                                        total: allReactions?.total,
                                    })}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
                                {sortedReactions.map((reaction, index) => {
                                    const src = getSourceFromReaction(post.platform, reaction.type);
                                    return src ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            }}
                                            key={index}
                                        >
                                            <img src={src} title={reaction.type} />
                                            <Typography sx={{ ml: 1 }} variant="s14w500" className="color-bd">
                                                {reaction.count}
                                            </Typography>
                                        </Box>
                                    ) : null;
                                })}
                            </Box>
                        )
                    }
                    placement={'bottom'}
                >
                    <Box
                        sx={reactionsContainer}
                        onClick={(event) => {
                            if (isReactionsExists) {
                                onReactionsClick?.(event);
                            }
                        }}
                        className={c.reactionsContainer}
                    >
                        {sortedReactions
                            .filter((reaction, index) => index < 3)
                            .map((reaction, index) => {
                                const src = getSourceFromReaction(post.platform, reaction.type);
                                return src ? (
                                    <img
                                        src={src}
                                        style={{ zIndex: index, marginLeft: index ? '-3px' : null }}
                                        key={index}
                                    />
                                ) : null;
                            })}
                        <Typography variant="s14w500" className={`${c.reactionsCount} color-md`}>
                            {numberPrettier(post.counts.reactions)}
                        </Typography>
                    </Box>
                </Tooltip>
                <ElementsDivider dividerOverrides={{ height: 16, color: '#5e6974' }} elements={getInteractions()} />
            </Box>
            {!!post.labels?.length && !!(post.counts.shares || post.counts.comments || post.counts.reactions) && (
                <Divider sx={{ mt: 1 }} />
            )}
            {!!post.labels?.length && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: post.counts.shares || post.counts.comments || post.counts.reactions ? 1.5 : 2.6,
                    }}
                >
                    <Typography variant="s14w500" sx={{ mr: 0.5 }}>
                        {i18n.translate('Topics')}
                    </Typography>
                    <Tooltip
                        placement="bottom-start"
                        content={<Typography>{i18n.translate('AI Generated')}</Typography>}
                    >
                        <Box sx={{ display: 'flex' }}>
                            <SessionBeginIcon className={c.sessionBeginIcon} />
                        </Box>
                    </Tooltip>
                    <Typography variant="s14w500" sx={{ lineHeight: '22px', mr: 1 }}>
                        :
                    </Typography>
                    <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', columnGap: 1 }} ref={containerRef}>
                        {post.labels
                            .slice(0, tooltipTopicsCount ? -1 * tooltipTopicsCount : post.labels.length)
                            .map((label, index) => (
                                <Topic key={index} label={label} />
                            ))}
                        {!!tooltipTopicsCount && (
                            <Tooltip
                                placement="bottom"
                                content={
                                    <Box>
                                        {post.labels
                                            .slice(post.labels.length - tooltipTopicsCount, post.labels.length)
                                            .map((label, index) => (
                                                <Topic key={index} label={label} />
                                            ))}
                                    </Box>
                                }
                            >
                                <Box sx={{ alignSelf: 'center' }}>
                                    <Box className={c.tooltipTopicsCount}>
                                        <Typography variant="s14w500">{`+${tooltipTopicsCount}`}</Typography>
                                    </Box>
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default PostInteractions;

const useStyles = tss
    .withParams<{ commentsExists: boolean; reactionsExists: boolean }>()
    .create(({ theme, commentsExists, reactionsExists }) => ({
        commentsContainer: {
            'display': 'flex',
            'alignItems': 'center',
            'color': theme.palette.clbtMuted.dark,
            'padding': '0px 4px',
            'borderRadius': '4px',
            '&:hover': commentsExists
                ? {
                      backgroundColor: theme.palette.clbtMuted.doubleHover,
                  }
                : null,
            '&:active': commentsExists
                ? {
                      backgroundColor: theme.palette.clbtBrand.lightest,
                  }
                : null,
            'cursor': commentsExists ? 'pointer' : null,
        },
        shareIcon: {
            fill: theme.palette.clbtMuted.dark,
        },
        reactionsContainer: {
            'display': 'flex',
            'alignItems': 'center',
            'color': theme.palette.clbtMuted.dark,
            'padding': '0px 4px',
            'borderRadius': '4px',
            '&:hover': reactionsExists
                ? {
                      backgroundColor: theme.palette.clbtMuted.doubleHover,
                  }
                : null,
            '&:active': reactionsExists
                ? {
                      backgroundColor: theme.palette.clbtBrand.lightest,
                  }
                : null,
            'cursor': reactionsExists ? 'pointer' : null,
        },
        reactionsCount: {
            marginLeft: theme.spacing(0.5),
            color: theme.palette.clbtMuted.dark,
            cursor: reactionsExists ? 'pointer' : null,
        },
        sessionBeginIcon: {
            fill: theme.palette.clbtBrand.main,
        },
        tooltipTopicsCount: {
            backgroundColor: theme.palette.clbtMuted.soft,
            height: 24,
            width: 'fit-content',
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
        },
    }));
